// Module-level navigation for services (auth expiry, notifications) that live
// outside the component tree. The root layout registers the router's navigate.
type NavigateFn = (to: string, opts?: { replace?: boolean }) => void;

let impl: NavigateFn | null = null;
// A navigation requested before the router mounted (bootstrap: loadSession runs
// before <App/> renders). Replayed by registerNavigate. It used to fall back to a
// full page load, which reload-looped when the session died: every boot hit the
// dead session, asked for /login, and reloaded before the router could render it.
let pending: { to: string; opts?: { replace?: boolean } } | null = null;

export function registerNavigate(fn: NavigateFn): void {
  impl = fn;
  if (pending) {
    const p = pending;
    pending = null;
    fn(p.to, { replace: true, ...p.opts });
  }
}

/** Navigate to an in-app path ("/tickets/42?x=1"). Before the router is up the
 *  request is queued and replayed once it registers (no full page load). */
export function navigate(to: string, opts?: { replace?: boolean }): void {
  if (impl) impl(to, opts);
  else pending = { to, opts };
}

/** The tenant base path from the injected <base href> ("/acme"; "" at root). */
export function basePath(): string {
  try {
    return new URL(document.baseURI).pathname.replace(/\/+$/, '');
  } catch {
    return '';
  }
}

/** The current in-app URL (path + query), without the tenant base path. */
export function currentAppUrl(): string {
  const base = basePath();
  let path = window.location.pathname;
  if (base && path.toLowerCase().startsWith(base.toLowerCase())) path = path.slice(base.length) || '/';
  return path + window.location.search;
}
